import styled, { css } from "styled-components";
import { connect } from "react-redux";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { List, X, UserCircle } from "@phosphor-icons/react";

import { signInAPI, signOutAPI, showLoginModal } from "../store/actions";
import Registration from "./Registration";
import { Button } from "./Shared/ui";

const NAV_ITEMS = [
  { label: "Home", hash: "#home" },
  { label: "About", hash: "#about" },
  { label: "Contact", hash: "#contact" },
];

const Header = ({ user, hideRegistrationPopup, signOut, showLoginModal }) => {
  const location = useLocation();
  const isHome = ["/", "/home", "/login", "/register"].includes(location.pathname);

  const [isScrolled, setIsScrolled] = useState(!isHome);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Switch the header to its solid state once the top of the page leaves the
  // viewport. A 1px sentinel + IntersectionObserver avoids scroll listeners.
  useEffect(() => {
    if (!isHome) return undefined;
    const sentinel = document.getElementById("header-sentinel");
    if (!sentinel || !("IntersectionObserver" in window)) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setIsScrolled(!entry.isIntersecting),
      { rootMargin: "80px 0px 0px 0px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isHome]);

  useEffect(() => {
    document.body.classList.toggle("no-scroll", isMenuOpen);
    return () => document.body.classList.remove("no-scroll");
  }, [isMenuOpen]);

  const hrefFor = (hash) => (isHome ? hash : `/${hash}`);
  const closeMenu = () => setIsMenuOpen(false);

  const authButton = user ? (
    <Button $variant={isScrolled ? "ghost" : "light"} onClick={signOut}>Log out</Button>
  ) : (
    <Button $variant={isScrolled ? "primary" : "light"} onClick={showLoginModal}>Log in</Button>
  );

  return (
    <>
      <Sentinel id="header-sentinel" aria-hidden="true" />
      <Bar $solid={isScrolled || isMenuOpen}>
        <Inner>
          <Brand href={hrefFor("#home")} onClick={closeMenu}>
            Vinayaka <span>Matrimony</span>
          </Brand>

          <DesktopNav aria-label="Main">
            {NAV_ITEMS.map((item) => (
              <NavLink key={item.hash} href={hrefFor(item.hash)}>
                {item.label}
              </NavLink>
            ))}
          </DesktopNav>

          <Actions>
            <Avatar href="/account" aria-label="My account">
              {user?.photoURL ? (
                <img src={user.photoURL} alt="" />
              ) : (
                <UserCircle size={30} weight="light" />
              )}
            </Avatar>
            <DesktopOnly>{authButton}</DesktopOnly>
            <MenuToggle
              type="button"
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMenuOpen}
              onClick={() => setIsMenuOpen((open) => !open)}
            >
              {isMenuOpen ? <X size={24} /> : <List size={24} />}
            </MenuToggle>
          </Actions>
        </Inner>
      </Bar>

      <MobilePanel $open={isMenuOpen} aria-hidden={!isMenuOpen}>
        <nav aria-label="Mobile">
          {NAV_ITEMS.map((item, i) => (
            <MobileLink
              key={item.hash}
              href={hrefFor(item.hash)}
              onClick={closeMenu}
              style={{ "--i": i }}
              tabIndex={isMenuOpen ? 0 : -1}
            >
              {item.label}
            </MobileLink>
          ))}
          <MobileLink href="/account" style={{ "--i": 3 }} tabIndex={isMenuOpen ? 0 : -1}>
            My account
          </MobileLink>
        </nav>
        <MobileAuth style={{ "--i": 4 }}>
          {user ? (
            <Button $variant="ghost" $block onClick={() => { closeMenu(); signOut(); }}>Log out</Button>
          ) : (
            <Button $block onClick={() => { closeMenu(); showLoginModal(); }}>Log in</Button>
          )}
        </MobileAuth>
      </MobilePanel>

      {!hideRegistrationPopup && <Registration />}
    </>
  );
};

const Sentinel = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 1px;
  height: 1px;
  pointer-events: none;
`;

const Bar = styled.header`
  position: fixed;
  inset: 0 0 auto 0;
  z-index: 100;
  height: var(--header-h);
  color: #fefefe;
  background: transparent;
  border-bottom: 1px solid transparent;
  transition:
    background-color 0.4s var(--ease-out),
    border-color 0.4s var(--ease-out),
    color 0.3s ease,
    box-shadow 0.4s var(--ease-out);

  ${({ $solid }) =>
    $solid &&
    css`
      color: var(--text);
      background: color-mix(in srgb, var(--bg) 82%, transparent);
      backdrop-filter: blur(16px) saturate(160%);
      -webkit-backdrop-filter: blur(16px) saturate(160%);
      border-bottom-color: var(--border);
      box-shadow: 0 10px 30px -24px rgb(36 48 94 / 0.5);

      @supports not (backdrop-filter: blur(1px)) {
        background: var(--bg);
      }
    `}
`;

const Inner = styled.div`
  height: 100%;
  max-width: var(--max-w);
  margin: 0 auto;
  padding: 0 var(--gutter);
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 16px;

  @media (max-width: 860px) {
    grid-template-columns: 1fr auto;
  }
`;

const Brand = styled.a`
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.02em;
  text-decoration: none;
  color: inherit;
  white-space: nowrap;

  span {
    font-weight: 400;
  }

  @media (max-width: 400px) {
    font-size: 19px;
  }
`;

const DesktopNav = styled.nav`
  display: flex;
  gap: 4px;

  @media (max-width: 860px) {
    display: none;
  }
`;

const NavLink = styled.a`
  position: relative;
  padding: 8px 16px;
  font-weight: 600;
  font-size: 15px;
  text-decoration: none;
  color: inherit;
  opacity: 0.88;
  transition: opacity 0.2s ease;

  &::after {
    content: "";
    position: absolute;
    left: 16px;
    right: 16px;
    bottom: 2px;
    height: 2px;
    border-radius: 2px;
    background: var(--coral);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.4s var(--ease-out);
  }

  &:hover {
    opacity: 1;
  }

  &:hover::after {
    transform: scaleX(1);
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
`;

const DesktopOnly = styled.div`
  @media (max-width: 860px) {
    display: none;
  }

  button {
    min-height: 42px;
    padding: 0 22px;
    font-size: 15px;
  }
`;

const Avatar = styled.a`
  display: inline-flex;
  width: 38px;
  height: 38px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: inherit;
  transition: transform 0.3s var(--ease-out);

  &:hover {
    transform: scale(1.06);
  }

  img {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid var(--butter);
  }
`;

const MenuToggle = styled.button`
  display: none;
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid currentColor;
  background: transparent;
  color: inherit;
  cursor: pointer;

  @media (max-width: 860px) {
    display: inline-flex;
  }
`;

const MobileLink = styled.a`
  font-family: var(--font-display);
  font-size: 34px;
  font-weight: 600;
  letter-spacing: -0.02em;
  padding: 12px 0;
  color: var(--text);
  text-decoration: none;
  border-bottom: 1px solid var(--border);
  opacity: 0;
  transform: translateY(12px);
  transition:
    opacity 0.5s var(--ease-out),
    transform 0.5s var(--ease-out),
    color 0.2s ease;
  transition-delay: calc(var(--i) * 50ms + 80ms);

  &:active {
    color: var(--coral);
  }
`;

const MobileAuth = styled.div`
  opacity: 0;
  transform: translateY(12px);
  transition:
    opacity 0.5s var(--ease-out),
    transform 0.5s var(--ease-out);
  transition-delay: calc(var(--i) * 50ms + 80ms);
`;

const MobilePanel = styled.div`
  position: fixed;
  inset: var(--header-h) 0 0 0;
  z-index: 150;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 24px var(--gutter) calc(32px + env(safe-area-inset-bottom));
  background: var(--bg);
  opacity: 0;
  visibility: hidden;
  transform: translateY(-8px);
  transition:
    opacity 0.35s var(--ease-out),
    transform 0.45s var(--ease-out),
    visibility 0s linear 0.45s;

  nav {
    display: flex;
    flex-direction: column;
  }

  ${({ $open }) =>
    $open &&
    css`
      opacity: 1;
      visibility: visible;
      transform: none;
      transition:
        opacity 0.35s var(--ease-out),
        transform 0.45s var(--ease-out),
        visibility 0s;

      ${MobileLink}, ${MobileAuth} {
        opacity: 1;
        transform: none;
      }
    `}

  @media (min-width: 861px) {
    display: none;
  }
`;

// REDUX CONNECTION
const mapStateToProps = (state) => ({
  user: state.userState.user,
});

const mapDispatchToProps = (dispatch) => ({
  signIn: () => dispatch(signInAPI()),
  signOut: () => dispatch(signOutAPI()),
  showLoginModal: () => dispatch(showLoginModal())
});

export default connect(mapStateToProps, mapDispatchToProps)(Header);
